import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthenticationService } from '../services/authentication/authentication.service';
import {Observable, switchMap} from 'rxjs';
import {catchError} from 'rxjs/operators';

export interface AuthGuardConfig {
  loginRedirectUrl?: string;
  forbiddenRedirectUrl?: string;
  requiredRoles?: string[];
}

export const DEFAULT_AUTH_GUARD_CONFIG: AuthGuardConfig = {
  loginRedirectUrl: '/login',
  forbiddenRedirectUrl: '/forbidden'
};

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);

  // Get the auth guard configuration from the route data or use defaults
  const config: AuthGuardConfig = {
    ...DEFAULT_AUTH_GUARD_CONFIG,
    ...(route.data['authGuardConfig'] || {})
  };

  console.log('authGuard', `isAuthenticated: ${authService.isAuthenticated}`, `isTokenExpired ${authService.isTokenExpired()}`, `isRefreshTokenTime: ${authService.isRefreshTokenTime()}`, `isRefreshTokenExpired: ${authService.isRefreshTokenExpired()}`);
  if (authService.isAuthenticated && (authService.isTokenExpired() || authService.isRefreshTokenTime()) && !authService.isRefreshTokenExpired()) {
    console.log('refreshing token guard');
    return authService.doRefreshToken().pipe(
      switchMap(() => {
        // after refresh, validate the authentication
        return validateAuthentication(authService, config, router, route, state);
      }),
      catchError(() => {
        // if refresh fails, logout
        authService.logout();
        router.navigate([config.loginRedirectUrl], {
          queryParams: {returnUrl: state.url}
        });
        return new Observable<boolean>((observer) => observer.next(false));
      })
    );

  }

  return validateAuthentication(authService, config, router, route, state);
}

function validateAuthentication(authService: AuthenticationService,
                                config: AuthGuardConfig,
                                router: Router,
                                route: ActivatedRouteSnapshot,
                                state: RouterStateSnapshot
) {
  console.log('validating authentication');
  // Check if the user is authenticated
  if (!authService.isAuthenticated) {
    // Redirect to login page
    authService.logout(); // cleanup
    router.navigate([config.loginRedirectUrl], {
      queryParams: { returnUrl: state.url }
    });
    return new Observable<boolean>((observer) => observer.next(false));
  }

  // If no roles are required, allow access
  if (!config.requiredRoles || config.requiredRoles.length === 0) {
    return new Observable<boolean>((observer) => observer.next(true));
  }

  // Check if the user has the required roles
  const userRoles = authService.userData?.roles || [];
  if (!authService.hasAnyRole(userRoles)) {
    // Redirect to forbidden page
    router.navigate([config.forbiddenRedirectUrl]);
    return new Observable<boolean>((observer) => observer.next(false));
  }

  return new Observable<boolean>((observer) => observer.next(true));
}
